import Service from '@ember/service';
import type { AxiosInstance, AxiosStatic, AxiosHeaders } from 'axios';
import { getOwner } from '@ember/application';
import type { AdapterRegistry } from 'ember-data/types/registries/adapter';

export interface IErrorResponse {
  title: string;
  detail: string;
}

export interface IErrorResult {
  isError: true;
  statusCode?: number;
  errors?: IErrorResponse[];
}

export default class HttpClientService extends Service {
  instance?: AxiosInstance;
  isAxiosError?: AxiosStatic['isAxiosError'];

  async getJSONApi<T>(
    url: string,
    params?: Record<string, string>,
  ): Promise<T | IErrorResult | undefined> {
    const instance = await this.getInstance();

    try {
      const request = await instance.get(`/${url}`, { params });

      return request.data;
    } catch (err) {
      return this.toErrorResult(err);
    }
  }

  async postJSONApi<T>(
    url: string,
    data: object,
  ): Promise<T | IErrorResult | undefined> {
    const instance = await this.getInstance();

    try {
      const request = await instance.post(`/${url}`, {
        data,
      });

      return request.data;
    } catch (err) {
      return this.toErrorResult(err);
    }
  }

  async deleteJSONApi(url: string): Promise<true | IErrorResult | undefined> {
    const instance = await this.getInstance();

    try {
      await instance.delete(`/${url}`);

      return true;
    } catch (err) {
      return this.toErrorResult(err);
    }
  }

  private toErrorResult(err: unknown): IErrorResult | undefined {
    if (this.isAxiosError?.(err) && err.response) {
      return {
        isError: true,
        statusCode: err.response.status,
        errors: err.response.data?.errors,
      };
    }
  }

  public async getInstance(): Promise<AxiosInstance> {
    if (this.instance) {
      return this.instance;
    }

    const { default: axios, isAxiosError } = await import('axios');

    const owner = getOwner(this);

    const adapter = owner?.lookup(
      'adapter:application',
    ) as AdapterRegistry['application'];

    this.instance = axios.create({
      baseURL: adapter.host,
      headers: adapter.headers as AxiosHeaders,
    });

    this.isAxiosError = isAxiosError;

    return this.instance;
  }
}

declare module '@ember/service' {
  interface Registry {
    'http-client': HttpClientService;
  }
}
